package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceArtefactsResponse
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.awaitNetworkResponse
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import org.w3c.fetch.ERROR
import org.w3c.fetch.NO_STORE
import org.w3c.fetch.RequestCache
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.fetch.RequestRedirect
import org.w3c.fetch.SAME_ORIGIN
import org.w3c.files.Blob

// UC-015. Artefact status (steps 2-5), the canonical invoice PDF (step 4), the confirmed
// work-report request (steps 6-7) and the requested report. Retrieving a PDF may generate it on
// the server; the browser never renders or stores one itself.
interface InvoiceArtefactPort {
    suspend fun artefacts(organisationId: String, invoiceId: String): InvoiceArtefactsResponse

    suspend fun invoicePdf(organisationId: String, invoiceId: String): InvoicePdf

    suspend fun requestWorkReport(
        organisationId: String,
        invoiceId: String,
    ): InvoiceArtefactsResponse

    suspend fun workReport(organisationId: String, invoiceId: String): InvoicePdf
}

val LocalInvoiceArtefactPort =
    staticCompositionLocalOf<InvoiceArtefactPort> {
        error("Invoice artefact port must be supplied by the application")
    }

// A completely received `application/pdf` answer with the server's file name (A8).
class InvoicePdf(val fileName: String, val content: Blob)

// A success answer that is not a complete PDF; nothing is presented as ready (A8).
class InvoicePdfIncomplete : Exception("Invoice PDF incomplete")

class BrowserInvoiceArtefactPort : InvoiceArtefactPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun artefacts(organisationId: String, invoiceId: String) =
        status(invoiceId, request(organisationId, artefactPath(invoiceId)))

    override suspend fun invoicePdf(organisationId: String, invoiceId: String) =
        pdf(organisationId, "${artefactPath(invoiceId)}/invoice-pdf", "Rechnung.pdf")

    // A4 is client-side: only a confirmed request reaches the server.
    override suspend fun requestWorkReport(organisationId: String, invoiceId: String) =
        status(invoiceId, request(organisationId, "${artefactPath(invoiceId)}/work-report", ""))

    override suspend fun workReport(organisationId: String, invoiceId: String) =
        pdf(organisationId, "${artefactPath(invoiceId)}/work-report", "Arbeitsrapport.pdf")

    private fun status(invoiceId: String, text: String) =
        json.decodeFromString<InvoiceArtefactsResponse>(text).also {
            require(it.invoiceId.equals(invoiceId, ignoreCase = true))
        }

    // Only a complete PDF body counts; an interrupted body or another content type is A8.
    private suspend fun pdf(
        organisationId: String,
        path: String,
        fallbackName: String,
    ): InvoicePdf {
        require(validOrganisationId(organisationId))
        val response =
            window
                .fetch(
                    "/api/v1/organisations/$organisationId/invoices$path",
                    RequestInit(
                        method = "GET",
                        credentials = RequestCredentials.SAME_ORIGIN,
                        cache = RequestCache.NO_STORE,
                        redirect = RequestRedirect.ERROR,
                    ),
                )
                .awaitNetworkResponse()
        val status = response.status.toInt()
        if (status != HTTP_OK)
            throw json.invoiceFailure(status, response.text().awaitNetworkResponse())
        val type = response.headers.get("Content-Type")?.substringBefore(';')?.trim()
        val content = response.blob().awaitNetworkResponse()
        if (!type.equals(PDF_TYPE, ignoreCase = true) || content.size.toInt() == 0)
            throw InvoicePdfIncomplete()
        return InvoicePdf(
            fileName(response.headers.get("Content-Disposition"), fallbackName),
            content,
        )
    }

    private suspend fun request(
        organisationId: String,
        path: String,
        body: String? = null,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(json, "/organisations/$organisationId/invoices$path", body) { status, text
            ->
            json.invoiceFailure(status, text)
        }
    }
}

private fun artefactPath(invoiceId: String): String {
    require(validInvoiceId(invoiceId))
    return "/$invoiceId/artefacts"
}

// The server's sanitised `inline; filename=…` name; anything else falls back to a generic name.
private fun fileName(disposition: String?, fallback: String): String =
    disposition
        ?.substringAfter("filename=", "")
        ?.substringBefore(';')
        ?.trim()
        ?.removeSurrounding("\"")
        ?.takeIf { FILE_NAME.matches(it) } ?: fallback

private val FILE_NAME = Regex("[A-Za-z0-9._-]{1,200}\\.pdf")
private const val HTTP_OK = 200
private const val PDF_TYPE = "application/pdf"

internal const val ARTEFACT_LINKED = "LINKED"
internal const val PDF_SNAPSHOT_UNAVAILABLE = "SNAPSHOT_UNAVAILABLE"
internal const val REPORT_NO_IMPORTED_WORK = "NO_IMPORTED_WORK"
internal const val REPORT_AVAILABLE = "AVAILABLE"
internal const val REPORT_UNAVAILABLE = "UNAVAILABLE"
